import { createFileRoute, Link } from '@tanstack/react-router';
import env from '../../../environment';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  LockClosedIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useCaseData } from '../../../assets/data/useCases';
import { basicHead, ogImage } from '../../../utility/head';
import NotFound from '../../../components/NotFound';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { LogoSlider } from '../../../components/LogoSlider';
import { getButtonStyles } from '@sc/shared-ui';
import { BottomCTA } from '../../../components/BottomCTA';
import { FaqList } from '../../../components/FaqList';
import { ResponsiveImage } from '../../../components/ResponsiveImage';
import ClockStopwatchIcon from '../../../assets/icons/clock-stopwatch.svg?react';
import EraserIcon from '../../../assets/icons/eraser.svg?react';
import EyeOverlayIcon from '../../../assets/shapes/eye-overlay.svg?react';

const defaultFaqs = [
  {
    q: 'What can I crawl?',
    a: 'Seed category directories, competitor sites, and known hosts. Stark Crawl discovers related hosts, crawls pages, and extracts the fields you define into structured rows.',
  },
  {
    q: 'Can I run separate projects for different markets?',
    a: 'Yes. Each project has its own hosts, schema, and access rules — keep category research, competitor monitoring, and prospect capture cleanly separated.',
  },
  {
    q: 'Can my whole research team use Stark Crawl?',
    a: 'Yes. Invite teammates to shared crawl projects so discovery, extraction, and exports live in one workspace instead of on one person’s laptop.',
  },
  {
    q: 'Is my crawl data safe and private?',
    a: 'Your projects and extracted data live in an isolated environment dedicated to your account, encrypted in transit and at rest. They are never used to train AI models.',
  },
];

export const Route = createFileRoute('/_marketing/use-cases/$slug')({
  component: RouteComponent,
  ssr: true,
  loader: ({ params, location }) => {
    const useCase = useCaseData.useCases.find(
      (u) => String(u.path) === params.slug
    );

    return {
      pathname: location.pathname,
      seo: {
        title: useCase ? useCase.title : 'Not Found - Stark Crawl',
        description: useCase?.metaDescription,
        image: useCase ? ogImage({ src: useCase.cardImage }) : undefined,
        preloadImage: useCase
          ? {
              src: useCase.cardImage,
              sizes: '(max-width: 640px) 80vw, 400px',
            }
          : undefined,
      },
    };
  },

  head: basicHead,
});

function RouteComponent() {
  const params = Route.useParams();
  const useCase = useCaseData.useCases.find(
    (u) => String(u.path) === params.slug
  );

  if (!useCase) return <NotFound to="/use-cases" />;

  return (
    <article className="overflow-x-hidden">
      {/* ─── Hero ────────────────────────────────────────────────────────── */}
      <section className="pt-15 lg:pt-20 pb-25 px-5">
        <div className="max-w-315 mx-auto flex flex-col lg:flex-row gap-15 items-center">
          {/* Left */}
          <div className="flex flex-col mt-2.5 mb-5 lg:max-w-130 order-2 lg:order-1">
            <Breadcrumbs
              items={[
                { label: 'Home', path: '/' },
                { label: "What it's for", path: '/use-cases' },
                { label: useCase.useCaseName },
              ]}
            />
            <h1 className="font-grotesque text-[2.625rem] leading-[1.15] lg:text-[4.375rem] lg:leading-none font-medium text-white py-5">
              {useCase.heroHeadline}{' '}
              <span className="text-primary-400">{useCase.heroHighlight}</span>
            </h1>
            <p className="text-lg leading-6.5 text-grey-300 mb-10">
              {useCase.heroKicker ?? useCase.blurb}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              {useCase.ctaType === 'contact' ? (
                <Link
                  to="/contact"
                  aria-label="Contact sales"
                  {...getButtonStyles({
                    type: 'primary',
                    size: 'xl',
                    additionalClassnames: 'gap-x-2',
                  })}
                >
                  Contact sales
                  <ArrowRightIcon className="w-4.5 h-4.5" aria-hidden="true" />
                </Link>
              ) : (
                <a
                  href={env.appUrl + '/register'}
                  target="_blank"
                  rel="noreferrer noopener"
                  aria-label="Try Stark Crawl for free (opens in new tab)"
                  {...getButtonStyles({
                    type: 'primary',
                    size: 'xl',
                    additionalClassnames: 'gap-x-2',
                  })}
                >
                  Try for free
                  <ArrowRightIcon className="w-4.5 h-4.5" aria-hidden="true" />
                </a>
              )}
            </div>
          </div>
          {/* Right: circular image */}
          <div className="flex-1 w-full min-w-0 order-1 flex items-center justify-center min-h-96 py-10">
            <div className="relative flex items-center justify-center w-full max-w-125 min-w-0">
              <EyeOverlayIcon
                className="z-40 w-full h-auto pointer-events-none"
                aria-hidden="true"
              />

              <ResponsiveImage
                src={useCase.cardImage}
                alt={useCase.useCaseName}
                className="absolute -top-[4.4%] z-10 w-4/5 aspect-square object-cover rounded-full"
                sizes="(max-width: 640px) 80vw, 400px"
                fetchPriority="high"
              />
            </div>
          </div>
        </div>
      </section>
      {/* ─── Trusted By ──────────────────────────────────────────────────── */}
      <section className="pb-15 px-5">
        <div className="max-w-315 mx-auto text-center">
          <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-5">
            Trusted by
          </p>
          <LogoSlider>
            <img src="/logos/aduro.svg" alt="Aduro" className="max-h-12.5" />
            <img src="/logos/able.svg" alt="Able" className="max-h-12.5" />
            <img
              src="/logos/bsquared.webp"
              alt="BSQuared"
              className="max-h-12.5 opacity-30"
            />
            <img src="/logos/tfp.svg" alt="TFP" className="max-h-12.5" />
            <img
              src="/logos/unibeez.svg"
              alt="Unibeez"
              className="max-h-12.5"
            />
          </LogoSlider>
        </div>
      </section>
      {/* ─── Problem & Solution ───────────────────────────────────────────── */}
      <section className="mb-25 pt-15 px-5 bg-background-tertiary">
        <div className="max-w-315 mx-auto text-center">
          <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-4">
            Problem &amp; Solution
          </p>
          <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-5 max-w-200 mx-auto">
            {useCase.secondaryHeadline}
          </h2>
          <p className="text-lg leading-6.5 text-grey-300 max-w-180 mx-auto mb-15">
            {useCase.blurb}
          </p>
          {/* Large screenshot */}
          <div className="relative rounded-t-md overflow-hidden border-8 border-b-0 border-grey-t-700 bg-background-secondary mx-auto max-w-240">
            <ResponsiveImage
              src={useCase.screenshot}
              alt={`${useCase.useCaseName} interface`}
              className="w-full -mb-1"
              sizes="(max-width: 1000px) 100vw, 944px"
              loading="lazy"
            />
          </div>
        </div>
      </section>
      {/* ─── Solution Blocks ──────────────────────────────────────────────── */}
      <section className="pb-25 px-5">
        <div className="max-w-315 mx-auto flex flex-col gap-25">
          {useCase.problemBlocks.map((block, idx) => {
            const isEven = idx % 2 === 0;
            const paragraphs = block.description.split(/\n\n+/);

            return (
              <div
                key={block.title}
                className="flex flex-col lg:flex-row gap-15 items-center"
              >
                <div
                  className={`flex flex-col gap-5 flex-1 order-2 ${
                    isEven ? 'lg:order-1' : 'lg:order-2'
                  }`}
                >
                  <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5">
                    Solution
                  </p>
                  <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[3rem] lg:leading-[1.2] font-medium text-white">
                    {block.title}
                  </h2>
                  {paragraphs.map((p, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-base leading-6 text-grey-200"
                    >
                      {p}
                    </p>
                  ))}
                </div>

                <div
                  className={`flex-1 flex justify-center order-1 ${
                    isEven ? 'lg:order-2' : 'lg:order-1'
                  }`}
                >
                  <ResponsiveImage
                    src={block.image}
                    alt={block.title}
                    sizes="(max-width: 640px) 100vw, 413px"
                    loading="lazy"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>
      {/* ─── How It Works (rendered whenever stepBlocks are defined) ──────── */}
      {!!useCase.stepBlocks?.blocks.length && (
        <section className="pb-25 px-5">
          <div className="max-w-315 mx-auto">
            <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-3 text-center">
              How it works
            </p>
            <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-15 text-center">
              {useCase.stepBlocks.headline}
            </h2>
            <ul
              className="flex flex-col lg:flex-row gap-y-10 gap-x-10"
              role="list"
            >
              {useCase.stepBlocks.blocks.map(
                ({ title, description, time }, idx) => (
                  <li key={title} className="relative">
                    <h6
                      className="font-grotesque text-[10rem] leading-25 -top-4 opacity-10 absolute font-bold text-primary-t-600 select-none block"
                      aria-hidden="true"
                    >
                      {idx + 1}
                    </h6>
                    <h3 className="font-grotesque mt-18 text-[1.875rem] leading-9 font-semibold text-white relative">
                      {title}
                    </h3>
                    <p className="text-base leading-6 text-grey-200 mt-5 mb-5">
                      {description}
                    </p>
                    {!!time && (
                      <div className="inline-flex items-center gap-2 bg-primary-1000 border border-primary-700 rounded-full px-4 py-2">
                        <ClockStopwatchIcon
                          className="w-4 h-4 text-primary-300"
                          aria-hidden="true"
                        />
                        <span className="font-open text-xs leading-5 text-primary-300">
                          {time}
                        </span>
                      </div>
                    )}
                  </li>
                )
              )}
            </ul>
          </div>
        </section>
      )}
      {/* ─── Private & Secure ─────────────────────────────────────────────── */}
      <section className="pb-25 px-5">
        <div className="max-w-315 mx-auto flex flex-col lg:flex-row gap-10 items-center">
          <div className="flex flex-col gap-5 items-start w-full lg:w-115 order-2 lg:order-1">
            <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5">
              Private &amp; secure
            </p>
            <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white">
              Your data stays yours
            </h2>
            <p className="text-base leading-6 text-grey-200">
              Your data is never used to train models or shared with third
              parties. It lives in an isolated environment dedicated to your
              account.
            </p>
            <ul className="flex flex-col gap-5 items-start" role="list">
              <li className="flex gap-2 items-center">
                <ShieldCheckIcon
                  className="w-5 h-5 text-grey-400 shrink-0"
                  aria-hidden="true"
                />
                <p className="font-open text-base leading-6 text-white">
                  Encrypted in transit and at rest
                </p>
              </li>
              <li className="flex gap-2 items-center">
                <LockClosedIcon
                  className="w-5 h-5 text-grey-400 shrink-0"
                  aria-hidden="true"
                />
                <p className="font-open text-base leading-6 text-white">
                  Completely isolated AI processing
                </p>
              </li>
              <li className="flex gap-2 items-center">
                <EraserIcon
                  className="w-5 h-5 text-grey-400 shrink-0"
                  aria-hidden="true"
                />
                <p className="font-open text-base leading-6 text-white">
                  Never used to train AI models
                </p>
              </li>
            </ul>
            <div className="flex gap-3 flex-wrap">
              <Link
                to="/privacy"
                {...getButtonStyles({
                  type: 'basic',
                  size: 'large',
                })}
              >
                Privacy policy
              </Link>
            </div>
          </div>
          <div className="flex-1 w-full min-w-0 order-1 flex items-center justify-center min-h-96 py-10">
            <div className="relative flex flex-col items-center justify-center gap-8 w-full min-w-0">
              <div className="relative flex items-center justify-center w-full max-w-125 min-w-0">
                <EyeOverlayIcon
                  className="z-40 w-full h-auto pointer-events-none"
                  aria-hidden="true"
                />
                <ResponsiveImage
                  src="/images/security.png"
                  alt="Stark Crawl security overlay"
                  className="absolute -top-[4.4%] z-10 w-4/5 aspect-square object-cover rounded-full"
                  sizes="400px"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ─── Capabilities (optional) ──────────────────────────────────────── */}
      {!!useCase.capabilities?.length && (
        <section className="pb-25 px-5">
          <div className="max-w-315 mx-auto">
            <p className="font-grotesque text-xl font-bold text-primary-400 uppercase leading-5 mb-3 text-center">
              Capabilities
            </p>
            <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-15 text-center">
              Everything you need to ship
            </h2>
            <ul
              className="grid grid-cols-1 md:grid-cols-2 gap-5"
              role="list"
            >
              {useCase.capabilities.map((capability) => (
                <li
                  key={capability}
                  className="bg-background-tertiary rounded-md p-5 flex gap-4 items-start"
                >
                  <CheckCircleIcon
                    className="w-6 h-6 text-primary-400 shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <p className="font-open text-base leading-6.5 text-white">
                    {capability}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
      {/* ─── FAQs ────────────────────────────────────────────────────────── */}
      <section className="pb-25 pt-10 px-5">
        <div className="max-w-315 mx-auto">
          <h2 className="font-grotesque text-4xl leading-[1.2] lg:text-[4rem] lg:leading-16 font-medium text-white mb-15 text-center">
            FAQs
          </h2>
          <FaqList faqs={useCase.faqs ?? defaultFaqs} />
        </div>
      </section>
      {/* ─── Bottom CTA ──────────────────────────────────────────────────── */}
      {useCase.ctaType === 'contact' ? (
        <BottomCTA
          title={
            <>
              Build it on Stark Crawl, bill it{' '}
              <span className="text-primary-400">your way</span>
            </>
          }
          body="Talk to us about a custom rate, integration support, and how to wrap our infrastructure into your own product."
          primaryLabel="Contact sales"
          primaryTo="/contact"
          primaryExternal={false}
        />
      ) : (
        <BottomCTA title={useCase.ctaTitle} body={useCase.ctaBody} />
      )}
    </article>
  );
}
